import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import QmsDocumentPreviewPane from "./QmsDocumentPreviewPane";
import QmsVaultPeoplePanel from "./QmsVaultPeoplePanel";
import QmsVaultAssociatedDocsPanel from "./QmsVaultAssociatedDocsPanel";
import QmsVaultCommentsPanel from "./QmsVaultCommentsPanel";
import QmsVaultQuizPanel from "./QmsVaultQuizPanel";
import QmsVaultVersionHistoryPanel from "./QmsVaultVersionHistoryPanel";
import QmsVaultAuditLogPanel from "./QmsVaultAuditLogPanel";
import QmsPublishStepperPanel from "./QmsPublishStepperPanel";
import {
  fetchMyVaultDocumentById,
  publishVaultDocument,
  saveDocumentComment,
  editDocumentComment,
  deleteDocumentComment,
  saveDocumentLibraryStaff,
  saveMyLearningStaff,
} from "./qmsService";
import {
  apiMessage,
  firstSuccessStoryRow,
  formatVaultApiError,
  getApiTable,
  isApiSuccess,
} from "./qmsApiUtils";
import { PUBLISH_WORKSPACE_PANELS } from "./qmsPublishWorkspacePanels";
import { canOpenPublishWorkspace } from "./qmsPublishUtils";
import {
  getVaultDocumentListId,
  getVaultDocumentTitle,
  getVaultDocumentIdAnchorHtml,
  getVaultStageClass,
  getVaultWorkspaceDocumentDetails,
} from "./qmsVaultUtils";
import {
  buildAssociatedDocPreviewRow,
  getVaultCommentMentionCandidates,
  getVaultWorkflowActions,
  isAssociatedDocPreview,
  isVaultHistoricalPreview,
  patchVaultWorkspaceDocumentLog,
  patchVaultWorkspaceStaffTables,
  getVaultStaffTrainingAdditionalRoles,
} from "./qmsVaultWorkspaceUtils";
import {
  buildLibraryPublishStaffRows,
  buildMyLearningPublishStaffRows,
  getAssignedStaffIdSet,
  getDocumentLibraryEligibleStaffIdSet,
  getMyLearningEligibleStaffIdSet,
  promptDiscardMyLearningChanges,
  serializeMyLearningAssignmentPayload,
} from "./qmsPublishStaffUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";

function QmsPublishWorkspaceModal({
  open,
  onClose,
  row,
  userData,
  activePanel = "publish",
  onPanelChange,
  onNotify,
  onPublished,
}) {
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [workspaceResponse, setWorkspaceResponse] = useState(null);
  const [panelSaving, setPanelSaving] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [railView, setRailView] = useState("details");
  const myLearningDirtyRef = useRef(false);

  const resolvedPanel = activePanel === "details" ? "publish" : activePanel;
  const isPreviewPanel = resolvedPanel === "preview";
  const isStaffPanel = resolvedPanel === "publish";

  const readOnlyWorkflow = useMemo(
    () => ({
      ...getVaultWorkflowActions(workspaceResponse || {}),
      canSaveReviewers: false,
      canSendReviewers: false,
      canCompleteReview: false,
      canSaveApprovers: false,
      canSendApprovers: false,
      canCompleteApproval: false,
      canManageAssociatedDocs: false,
      canManageQuiz: false,
    }),
    [workspaceResponse],
  );

  const headerStage = useMemo(() => {
    const info = getApiTable(workspaceResponse, "DocumentInfo")[0] || row || {};
    const stage = String(info.DocumentStage || row?.DocumentStage || "").trim();
    return stage && stage !== "—" ? stage : "";
  }, [workspaceResponse, row]);

  const canPublish = canOpenPublishWorkspace(row);

  const myLearningStaffRows = useMemo(
    () => buildMyLearningPublishStaffRows(workspaceResponse || {}),
    [workspaceResponse],
  );

  const myLearningStaffTrainingRoles = useMemo(
    () => getVaultStaffTrainingAdditionalRoles(workspaceResponse || {}),
    [workspaceResponse],
  );

  const libraryStaffRows = useMemo(
    () => buildLibraryPublishStaffRows(workspaceResponse || {}),
    [workspaceResponse],
  );

  useEffect(() => {
    if (!open) return;
    setRailView("details");
    myLearningDirtyRef.current = false;
  }, [open, row?.DocumentId]);

  const handleMyLearningDirtyChange = useCallback((dirty) => {
    myLearningDirtyRef.current = dirty;
  }, []);

  const confirmLeavePublishPanel = useCallback(
    (onLeave) => {
      if (resolvedPanel !== "publish" || !myLearningDirtyRef.current) {
        onLeave();
        return;
      }

      promptDiscardMyLearningChanges(showAlert, () => {
        myLearningDirtyRef.current = false;
        onLeave();
      });
    },
    [resolvedPanel, showAlert],
  );

  const handleRequestClose = useCallback(() => {
    confirmLeavePublishPanel(() => {
      myLearningDirtyRef.current = false;
      onClose?.();
    });
  }, [confirmLeavePublishPanel, onClose]);

  const handlePanelChange = useCallback(
    (panelId) => {
      if (resolvedPanel === "publish" && panelId !== "publish") {
        confirmLeavePublishPanel(() => {
          if (panelId !== "publish") {
            myLearningDirtyRef.current = false;
          }
          onPanelChange?.(panelId);
        });
        return;
      }

      onPanelChange?.(panelId);
    },
    [confirmLeavePublishPanel, onPanelChange, resolvedPanel],
  );

  useEffect(() => {
    if (!open || !row?.DocumentId || !userData?.StaffId) {
      return undefined;
    }

    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError("");
    setWorkspaceResponse(null);
    setPreviewRow(null);

    fetchMyVaultDocumentById(userData, row.DocumentId)
      .then((response) => {
        if (!cancelled) setWorkspaceResponse(response);
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailsError(error?.message || "Could not load document details.");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, row?.DocumentId, userData]);

  const notifySuccess = useCallback(
    (message) => onNotify?.({ title: "Success", message, type: "success" }),
    [onNotify],
  );

  const notifyError = useCallback(
    (message) => onNotify?.({ title: "Error", message, type: "danger" }),
    [onNotify],
  );

  const runCommentAction = useCallback(
    async (action, successMessage) => {
      setPanelSaving(true);
      try {
        const response = await action();
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }
        setWorkspaceResponse((prev) => patchVaultWorkspaceDocumentLog(prev || {}, response));
        notifySuccess(successMessage);
        return response;
      } catch (error) {
        notifyError(error?.message || "Request failed.");
        throw error;
      } finally {
        setPanelSaving(false);
      }
    },
    [notifyError, notifySuccess],
  );

  const saveMyLearningDraft = useCallback(
    async (draft) => {
      if (!row?.DocumentId) return;

      setPanelSaving(true);
      try {
        const payload = serializeMyLearningAssignmentPayload(
          draft,
          myLearningStaffRows,
          myLearningStaffTrainingRoles,
        );
        const response = await saveMyLearningStaff(userData, {
          documentId: row.DocumentId,
          staffIds: payload.staffIds,
        });
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }

        setWorkspaceResponse((prev) => patchVaultWorkspaceStaffTables(prev || {}, response));
        notifySuccess("My Learning access saved.");
      } catch (error) {
        notifyError(error?.message || "Save failed.");
        throw error;
      } finally {
        setPanelSaving(false);
      }
    },
    [myLearningStaffRows, myLearningStaffTrainingRoles, notifyError, notifySuccess, row?.DocumentId, userData],
  );

  const saveLibraryDraft = useCallback(
    async (assignedIds) => {
      if (!row?.DocumentId) return;

      setPanelSaving(true);
      try {
        const eligibleIds = getDocumentLibraryEligibleStaffIdSet(libraryStaffRows);
        const staffIds = [...(assignedIds instanceof Set ? assignedIds : new Set(assignedIds))]
          .map((id) => String(id))
          .filter((id) => id && eligibleIds.has(id))
          .join(",");

        const response = await saveDocumentLibraryStaff(userData, {
          documentId: row.DocumentId,
          staffIds,
        });
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }

        setWorkspaceResponse((prev) => patchVaultWorkspaceStaffTables(prev || {}, response));
        notifySuccess("Library staff saved.");
      } catch (error) {
        notifyError(error?.message || "Save failed.");
        throw error;
      } finally {
        setPanelSaving(false);
      }
    },
    [libraryStaffRows, notifyError, notifySuccess, row?.DocumentId, userData],
  );

  const handlePublish = async (payload) => {
    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) {
      if (!authRes?.cancelled) {
        notifyError("Two-factor authentication failed or was cancelled.");
      }
      return;
    }

    const publishReason = String(authRes?.Description || "").trim();
    if (!publishReason) {
      notifyError("A reason is required to publish this document.");
      return;
    }

    setPanelSaving(true);
    try {
      const myLearningEligibleIds = getMyLearningEligibleStaffIdSet(myLearningStaffRows);
      const libraryEligibleIds = getDocumentLibraryEligibleStaffIdSet(libraryStaffRows);
      const myLearningAccess = [...getAssignedStaffIdSet(myLearningStaffRows)]
        .filter((id) => myLearningEligibleIds.has(id))
        .join(",");
      const documentLibraryAccess = [...getAssignedStaffIdSet(libraryStaffRows)]
        .filter((id) => libraryEligibleIds.has(id))
        .join(",");

      const response = await publishVaultDocument(userData, {
        DocumentId: row.DocumentId,
        Comments: publishReason,
        IsMandatory: payload.isMandatory,
        IsQuizApplicable: payload.isQuizApplicable,
        ReviewDateNumber: payload.reviewDateNumber,
        ReviewDateSuffix: payload.reviewDateSuffix,
        QuizDueDate: payload.quizDueDate || payload.reviewDueDate,
        ReminderFrequency: payload.reminderFrequency,
        ReminderInterval: payload.reminderInterval,
        NotificationRulesJson: JSON.stringify(payload.notificationRules || []),
        MyLearningAccess: myLearningAccess,
        DocumentLibraryAccess: documentLibraryAccess,
      });
      if (!isApiSuccess(response)) {
        throw new Error(formatVaultApiError(apiMessage(response)));
      }
      const story = firstSuccessStoryRow(response);
      notifySuccess(apiMessage(response, story) || "Document published successfully.");
      onPublished?.(story);
      onClose?.();
    } catch (error) {
      notifyError(error?.message || "Publish failed.");
    } finally {
      setPanelSaving(false);
    }
  };

  const viewingHistoricalVersion = isVaultHistoricalPreview(previewRow, row);
  const viewingAssociatedDocument = isAssociatedDocPreview(previewRow);

  const returnToLatestVersion = useCallback(() => {
    setPreviewRow(null);
    onPanelChange?.("preview");
  }, [onPanelChange]);

  const openAssociatedDocPreview = useCallback(
    (docRow) => {
      setPreviewRow(buildAssociatedDocPreviewRow(docRow));
      onPanelChange?.("preview");
    },
    [onPanelChange],
  );

  const renderDetailsPanel = () => {
    if (detailsLoading) {
      return <p className="text-muted mb-0 small">Loading document details…</p>;
    }
    if (detailsError) {
      return <p className="text-danger mb-0 small">{detailsError}</p>;
    }

    const details = getVaultWorkspaceDocumentDetails(workspaceResponse || {}, row);
    const info = getApiTable(workspaceResponse, "DocumentInfo")[0] || row;

    return (
      <dl className="qms-vault-workspace__details-grid">
        <div className="qms-vault-workspace__details-id">
          <dt>Document ID</dt>
          <dd>
            <HoverPreview
              isHtml
              content={details.documentIdHelpHtml}
              anchorHtml={getVaultDocumentIdAnchorHtml(info)}
              plainTextForPreview={details.documentId}
              previewLength={details.documentId.length + 1}
              appendEllipsis={false}
              maxWidth={280}
              maxHeight={220}
              className="qms-vault-workspace__details-id-hover"
            />
          </dd>
        </div>
        <div>
          <dt>Title</dt>
          <dd>{details.displayTitle || "—"}</dd>
        </div>
        <div>
          <dt>Version</dt>
          <dd>{details.version || "—"}</dd>
        </div>
        <div>
          <dt>Stage</dt>
          <dd>{details.stage || "—"}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{details.owner || "—"}</dd>
        </div>
        <div>
          <dt>Modified</dt>
          <dd>{details.modified || "—"}</dd>
        </div>
      </dl>
    );
  };

  const renderCommentsRail = () => (
    <QmsVaultCommentsPanel
      active={railView === "comments"}
      variant="rail"
      documentId={row.DocumentId}
      userData={userData}
      staffOptions={getVaultCommentMentionCandidates(workspaceResponse || {})}
      saving={panelSaving}
      onSave={({ comment, previousCommentId, assignedStaff, commentPriority }) =>
        runCommentAction(
          () =>
            saveDocumentComment(userData, {
              DocumentId: row.DocumentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment posted.",
        )
      }
      onEdit={({ commentId, comment, assignedStaff, commentPriority, previousCommentId }) =>
        runCommentAction(
          () =>
            editDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment updated.",
        )
      }
      onDelete={({ commentId }) =>
        runCommentAction(
          () =>
            deleteDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
            }),
          "Comment deleted.",
        )
      }
    />
  );

  const renderSecondaryPanel = () => {
    if (detailsLoading && !workspaceResponse) {
      return (
        <div className="qms-doc-workspace__panel">
          <p className="text-muted mb-0 small">Loading workspace…</p>
        </div>
      );
    }

    switch (resolvedPanel) {
      case "publish":
        return (
          <QmsPublishStepperPanel
            myLearningStaff={myLearningStaffRows}
            myLearningStaffTrainingRoles={myLearningStaffTrainingRoles}
            libraryStaff={libraryStaffRows}
            workspaceResponse={workspaceResponse}
            row={row}
            loading={detailsLoading && !workspaceResponse}
            saving={panelSaving}
            canPublish={canPublish}
            onMyLearningSave={saveMyLearningDraft}
            onLibrarySave={saveLibraryDraft}
            onPublish={handlePublish}
            onMyLearningDirtyChange={handleMyLearningDirtyChange}
          />
        );
      case "reviewers":
        return (
          <QmsVaultPeoplePanel
            mode="reviewers"
            readOnly
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
          />
        );
      case "approvers":
        return (
          <QmsVaultPeoplePanel
            mode="approvers"
            readOnly
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
          />
        );
      case "quiz":
        return (
          <QmsVaultQuizPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
            userData={userData}
          />
        );
      case "associatedDocs":
        return (
          <QmsVaultAssociatedDocsPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
            onPreviewDocument={openAssociatedDocPreview}
          />
        );
      case "versions":
        return (
          <QmsVaultVersionHistoryPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
            onOpenVersion={(versionRow) => {
              setPreviewRow(versionRow);
              onPanelChange?.("preview");
            }}
          />
        );
      case "auditLog":
        return (
          <QmsVaultAuditLogPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
          />
        );
      default:
        return null;
    }
  };

  if (!open || !row) return null;

  return (
    <>
      <div
        className="modal fade show d-block qms-doc-workspace-modal qms-vault-workspace-modal qms-doc-lib-workspace-modal qms-publish-workspace-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="Publish document workspace"
      >
        <div className="modal-dialog qms-doc-workspace-modal__dialog">
          <div className="modal-content qms-doc-workspace-modal__content">
            <div className="qms-doc-workspace-modal__header">
              <div className="qms-doc-workspace-modal__header-main">
                <div className="qms-doc-workspace-modal__icon" aria-hidden="true">
                  <i className="fas fa-globe" />
                </div>
                <div>
                  <h5 className="qms-doc-workspace-modal__title">
                    {getVaultDocumentTitle(row)}
                  </h5>
                  <div className="qms-doc-workspace-modal__code-row">
                    <div className="qms-doc-workspace-modal__code">
                      {getVaultDocumentListId(row)}
                    </div>
                    {headerStage ? (
                      <span
                        className={`qms-doc-workspace-modal__stage ${getVaultStageClass(headerStage)}`}
                      >
                        {headerStage}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleRequestClose}
                aria-label="Close"
              />
            </div>

            <div className="qms-doc-workspace-modal__body">
              <nav
                className="qms-doc-workspace-modal__sidebar"
                aria-label="Publish workspace sections"
              >
                <ul className="qms-doc-workspace-modal__nav">
                  {PUBLISH_WORKSPACE_PANELS.map((panel) => {
                    const isActive = panel.id === resolvedPanel;
                    return (
                      <li key={panel.id}>
                        <button
                          type="button"
                          className={`qms-doc-workspace-modal__nav-item${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => {
                            if (panel.id === "preview" && previewRow) {
                              returnToLatestVersion();
                              return;
                            }
                            if (panel.id !== "preview" && panel.id !== "versions") {
                              setPreviewRow(null);
                            }
                            handlePanelChange(panel.id);
                          }}
                        >
                          <i className={panel.icon} aria-hidden="true" />
                          <span>{panel.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="qms-doc-workspace-modal__main qms-vault-workspace-modal__split">
                <div
                  className={`qms-vault-workspace__panel-main${
                    resolvedPanel === "preview" ? " is-preview" : ""
                  }${isStaffPanel ? " is-staff" : ""}`}
                >
                  <div className="qms-vault-workspace__panel-stack">
                    <div
                      className={`qms-vault-workspace__panel-layer${
                        isPreviewPanel ? " is-active" : ""
                      }`}
                      aria-hidden={!isPreviewPanel}
                    >
                      <div className="qms-doc-workspace__preview-pane">
                        {viewingAssociatedDocument || viewingHistoricalVersion ? (
                          <div className="qms-vault-workspace__version-banner">
                            <div className="qms-vault-workspace__version-banner-copy">
                              <i className="fas fa-eye" aria-hidden="true" />
                              <span>Preview mode</span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary qms-vault-workspace__version-banner-btn"
                              onClick={returnToLatestVersion}
                            >
                              Back to current document
                            </button>
                          </div>
                        ) : null}
                        <div className="qms-vault-workspace__preview-body">
                          <QmsDocumentPreviewPane
                            active={isPreviewPanel}
                            documentId={
                              previewRow?.DocumentId ||
                              previewRow?.DocumentIdRef ||
                              row.DocumentId ||
                              row.DocumentIdRef ||
                              ""
                            }
                            documentUrl={previewRow?.DocumentURL || row.DocumentURL}
                            documentExtension={
                              previewRow?.DocumentExtension || row.DocumentExtension
                            }
                            documentTitle={getVaultDocumentTitle(previewRow || row)}
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className={`qms-vault-workspace__panel-layer${
                        !isPreviewPanel ? " is-active" : ""
                      }`}
                      aria-hidden={isPreviewPanel}
                    >
                      <div className="qms-vault-workspace__secondary-panel">
                        {renderSecondaryPanel()}
                      </div>
                    </div>
                  </div>
                </div>

                <aside
                  className={`qms-vault-workspace__details-rail${
                    railView === "comments" ? " is-comments-mode" : ""
                  }`}
                  aria-label={railView === "comments" ? "Document comments" : "Document details"}
                >
                  <div className="qms-vault-workspace__rail-toggle" role="tablist" aria-label="Right panel">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "details"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "details" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("details")}
                    >
                      Details
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "comments"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "comments" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("comments")}
                    >
                      Comments
                    </button>
                  </div>

                  {railView === "details" ? (
                    <div className="qms-vault-workspace__rail-details-body">
                      {renderDetailsPanel()}
                    </div>
                  ) : (
                    <div className="qms-vault-workspace__rail-comments-body">
                      {renderCommentsRail()}
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}

export default QmsPublishWorkspaceModal;
